/**
 * Affiliate Links utilities.
 *
 * Generates trackable affiliate links and parses affiliate codes from URLs.
 *
 * URL format: https://marketplace.com/l/listing-slug/uuid?aff=AFFILIATE_CODE
 *
 * Affiliate codes stored in:
 *   - Admin config via AFFILIATE_CODES_JSON env var
 *   - { "PARTNER1": { name: "Partner 1", commission: 10 } }  (commission in %)
 *
 * Click tracking stored in:
 *   - localStorage: affc=AFFILIATE_CODE (captured by referralTracking pattern)
 *   - Server: POST /api/affiliates/track
 *
 * Conversion tracking:
 *   - On checkout completion, if aff cookie exists, record conversion
 *   - POST /api/affiliates/convert { code, transactionId, amount }
 */

const AFFILIATE_PARAM = 'aff';

/**
 * Build an affiliate URL by appending the affiliate code param.
 * @param {string} url — base listing/page URL
 * @param {string} affiliateCode
 * @returns {string}
 */
export const buildAffiliateUrl = (url, affiliateCode) => {
  if (!url || !affiliateCode) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${AFFILIATE_PARAM}=${encodeURIComponent(affiliateCode)}`;
};

/**
 * Extract affiliate code from URL or search params.
 * @param {string} search — window.location.search
 * @returns {string|null}
 */
export const extractAffiliateCode = (search) => {
  if (!search) return null;
  const params = new URLSearchParams(search);
  const code = params.get(AFFILIATE_PARAM);
  return code ? code.toUpperCase() : null;
};

/**
 * Save affiliate code to localStorage (30-day expiry).
 * @param {string} code
 */
export const saveAffiliateCode = (code) => {
  if (typeof localStorage === 'undefined') return;
  const data = { code, savedAt: Date.now() };
  localStorage.setItem('affiliateCode', JSON.stringify(data));
};

/**
 * Get saved affiliate code from localStorage if not expired.
 * @returns {string|null}
 */
export const getSavedAffiliateCode = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('affiliateCode');
    if (!raw) return null;
    const { code, savedAt } = JSON.parse(raw);
    const EXPIRY_MS = 30 * 24 * 3600 * 1000;
    if (Date.now() - savedAt > EXPIRY_MS) {
      localStorage.removeItem('affiliateCode');
      return null;
    }
    return code;
  } catch (_) {
    return null;
  }
};

/**
 * Clear the saved affiliate code (call after conversion).
 */
export const clearAffiliateCode = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('affiliateCode');
  }
};

/**
 * Parse affiliate codes from AFFILIATE_CODES_JSON env.
 * @returns {Object} — { CODE: { name, commission } }
 */
export const parseAffiliateCodes = () => {
  if (typeof process === 'undefined') return {};
  try {
    return JSON.parse(process.env.AFFILIATE_CODES_JSON || '{}');
  } catch (_) {
    return {};
  }
};
